"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRouter } from "next/navigation";
import { useToast } from "@/components/ui/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

interface AcknowledgmentModalProps {
  isOpen: boolean;
  userName: string;
}

export const AcknowledgmentModal = ({ isOpen, userName }: AcknowledgmentModalProps) => {
  const [open, setOpen] = useState(isOpen);
  const [confirmName, setConfirmName] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const router = useRouter();

  // Ensure modal can't be closed by escape key or clicking outside
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleContinue = async () => {
    // Add validation for name comparison if needed
    // if (confirmName.trim().toLowerCase() !== userName.toLowerCase()) {
    //   setError("Please enter your name exactly as shown to confirm.");
    //   return;
    // }

    if (!confirmName.trim()) {
      setError("Please enter your name to continue");
      return;
    }

    if (!isChecked) {
      setError("Please check the box to confirm you understand and agree to the terms");
      return;
    }

    try {
      const response = await fetch("/api/user/acknowledge", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ acknowledged: true }),
      });

      if (response.ok) {
        toast({
          title: "Acknowledgment Confirmed",
          description: "Thank you for confirming the acknowledgment.",
        });
        setOpen(false);
        router.refresh(); // Refresh the page to update UI state
      } else {
        throw new Error("Failed to save acknowledgment");
      }
    } catch (error) {
      console.error("Error saving acknowledgment:", error);
      toast({
        title: "Error",
        description: "There was a problem saving your acknowledgment. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(openState) => !openState && setOpen(false)}> {/* Allow closing via X button, but prevent outside click/escape */}
      <DialogContent 
        className="sm:max-w-md" 
        onInteractOutside={(e) => e.preventDefault()} // Prevent closing on outside click
        onEscapeKeyDown={(e) => e.preventDefault()} // Prevent closing with Escape key
      >
        <DialogHeader>
          <DialogTitle>Important Acknowledgment</DialogTitle>
          <DialogDescription>
            Please read the following information carefully before proceeding.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="p-4 overflow-y-auto rounded-md bg-muted max-h-64">
            {/* Updated Text Block */}
            <p className="mb-3">
              At this time, EquipmentShare and Equipment Zone are in the process of finalizing parameters for 2025. Once parameters are finalized, documents will be generated and sent to you for signature. Until then, we will not know how much equipment is available. Equipment will come at a first come, first serve basis, and will be allocated to those who have completed the intake process and have been approved for financing. Please check and sign that you have read and agree to the terms and conditions.
            </p>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms"
              checked={isChecked}
              onCheckedChange={(checked) => {
                setIsChecked(checked as boolean);
                if (checked) setError(""); // Clear error when checkbox is checked
              }}
              className="mt-1"
              aria-label="Agree to terms and conditions checkbox" // Added aria-label for accessibility
            />
            <Label
              htmlFor="terms"
              className="text-sm font-medium leading-tight cursor-pointer" // Added cursor-pointer
            >
              I have read and agree to the terms and conditions
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Type your full name to confirm acknowledgment:
            </Label>
            <Input
              id="name"
              value={confirmName}
              onChange={(e) => {
                setConfirmName(e.target.value);
                if (e.target.value.trim()) setError(""); // Clear error when user starts typing
              }}
              placeholder={`Type your name (${userName})`}
              className={error && !isChecked && !confirmName.trim() ? "border-red-500" : ""} // More specific error styling
              aria-required="true" // Added aria-required for accessibility
              aria-describedby="name-error" // Link input to error message
            />
            {error && <p id="name-error" className="text-sm text-red-500">{error}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button 
            type="button" 
            onClick={handleContinue}
            disabled={!isChecked || !confirmName.trim()} // Disable button if conditions aren't met
          >
            I Acknowledge & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};